import React, { useState } from 'react'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'

import ChecklistsFilterV2 from '../../checklists/filters/ChecklistsFilterV2'
import ConfigValuesFilter from '../../configs/filters/ConfigValuesFilter'
import FullPlacesFilterV2 from '../../places/filters/FullPlacesFilterV2'
import StatusesFilterV2 from '../../statuses/filters/StatusesFilterV2'
import UsersFilterV2 from '../../users/filters/UsersFilterV2'
import Editable from '../../../packages/kiti/components/form/Editable'
import Text from '../../../packages/kiti/components/presentation/Text'
import Icon from '../../../packages/kiti/components/shapes/Icon'
import { Grid } from '../../../packages/kiti/components/structure/Grid'
import { useIsMaster } from '@btime/react-session'
import { useChangeFilterValue, useFilterValue } from '@btime/react-contexts'
import DatesFilter from '../../../modules/serviceOrders/list/filters/DatesFilter'
import MinicrudsFilter from '../../minicruds/filters/MinicrudsFilterV2'
import AssetsFilterV2 from '../../assets/filters/AssetsFilterV2'
import TagsFilterV2 from '../../tags/filters/TagsFilterV2'

const getServiceOrderKanbanDateOptions = t => [
  { label: t('dateOptions.overdue6Plus'), value: { type: 'day', init: 999, end: 6, range_init: 6, mode: 'subtract' } },
  { label: t('dateOptions.overdue2to5'), value: { type: 'day', init: 5, end: 2, range_init: 2, mode: 'subtract' } },
  { label: t('dateOptions.overdue1'), value: { type: 'day', init: 1, end: 1, mode: 'subtract' } },
  { label: t('dateOptions.dueToday'), value: { type: 'day' } },
  { label: t('dateOptions.dueTomorrow'), value: { type: 'day', init: 0, end: 1, range_init: 1, mode: 'add' } },
  { label: t('dateOptions.due2to5'), value: { type: 'day', init: 0, end: 5, range_init: 2, mode: 'add' } },
  { label: t('dateOptions.due6to10'), value: { type: 'day', init: 0, end: 10, range_init: 6, mode: 'add' } },
  { label: t('dateOptions.due11to15'), value: { type: 'day', init: 0, end: 15, range_init: 11, mode: 'add' } },
  { label: t('dateOptions.due16to30'), value: { type: 'day', init: 0, end: 30, range_init: 16, mode: 'add' } },
  { label: t('dateOptions.due60Plus'), value: { type: 'day', init: 0, end: 999, range_init: 60, mode: 'add' } }
]

const InputField = ({ field, restrictToMaster, placeHolder, iconName, ...props }) => {
  const initialValue = useFilterValue(field, '')
  const isMaster = useIsMaster()
  const canShow = restrictToMaster ? isMaster : true
  const handleChange = useChangeFilterValue()
  const [value, setValue] = useState(initialValue)
  const iconColor = !!value ? 'dark1' : 'light5'
  const color = !!value ? 'dark1' : 'dark3'
  const onChange = value => {
    setValue(value)
    handleChange(field, value || undefined)
  }

  if (!canShow) return <></>

  return (
    <Grid align="center" gutter={0.1}>
      <Icon name={iconName} color={iconColor} />
      <Editable field={field} value={value} showSubmitButton onSubmit={onChange} alwaysFireOnSubmit {...props}>
        <Text color={color} weight={!!value ? 800 : 500}>
          {!!value?.length ? value : placeHolder}
        </Text>
      </Editable>
    </Grid>
  )
}

const FiltersList = ({ showExternalDataFilter, showContainsInNameFilter, isKanban }) => {
  const { t } = useTranslation('serviceOrders')
  const canUseDateKanbanSuggestion = useSessionPermission('canUseDateKanbanSuggestion')

  const defaultReasonEntities = ['approved', 'reproved', 'unproductive', 'canceled', 'paused']

  const selectedStatuses = useFilterValue('statusIds', undefined)
  const selectedStatusesNames = selectedStatuses?.map(entity => entity.name)

  return (
    <>
      <StatusesFilterV2 field="statusIds" />
      <MinicrudsFilter
        field="reasonIds"
        label={t('filters.statusReasons')}
        filter={{ entities: selectedStatusesNames || defaultReasonEntities }}
      />
      <ChecklistsFilterV2 />
      <UsersFilterV2 />
      <FullPlacesFilterV2 label={t('filters.place')} field="placeIds" placeLevelField="placeLevelIds" />
      <AssetsFilterV2 />
      <TagsFilterV2 />
      <ConfigValuesFilter
        label={t('filters.semaphore')}
        field="semaphoreKeys"
        icon="RiTrafficLightLine"
        configName="service_order_started_since_colors"
      />
      {isKanban && (
        <DatesFilter
          label={t('filters.dates')}
          field="scheduling"
          icon="BsCalendar"
          recommendedOptions={canUseDateKanbanSuggestion ? getServiceOrderKanbanDateOptions(t) : undefined}
        />
      )}
      <UsersFilterV2 label={t('filters.createdBy')} field="createdByIds" icon="RiEdit2Line" />
      {showExternalDataFilter && (
        <InputField
          field={'externalData'}
          restrictToMaster={true}
          placeHolder={t('filters.externalValue')}
          iconName={'RiExternalLinkFill'}
        />
      )}
      {showContainsInNameFilter && (
        <InputField field={'containsInName'} placeHolder={t('filters.containsInName')} iconName={'BsSearch'} />
      )}
    </>
  )
}

export default FiltersList
